import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";
import { Link } from "react-router-dom";
import { 
  Building, 
  Shield, 
  CheckCircle,
  Phone,
  Award,
  Clock,
  MapPin,
  Users,
  Target,
  FileText,
  Mail
} from "lucide-react";
import blueprintsImage from "@/assets/general-contractor-blueprints.jpg";
import projectsImage from "@/assets/general-contracting-projects.jpg";

const GeneralContractingServices = () => {
  const faqData = {
    faqs: [
      {
        question: "Do you provide residential general contracting in Toronto?",
        answer: "Yes. Blue Knight Contracting offers complete general contracting for residential projects in Toronto and the entire GTA, including custom homes, renovations, and additions."
      },
      {
        question: "Can Blue Knight handle both commercial and industrial projects?",
        answer: "Absolutely. We specialize in managing commercial office builds, retail developments, and large-scale industrial facilities across Ontario with comprehensive project management."
      },
      {
        question: "Do you serve smaller cities in Ontario as well?",
        answer: "Yes. In addition to major cities, we also provide general contracting services in surrounding towns and rural communities across Ontario."
      },
      {
        question: "What does your project management include?",
        answer: "Our project management includes complete coordination from design to completion, budgeting and scheduling, subcontractor coordination, quality control, and compliance with Ontario building codes."
      }
    ]
  };

  const serviceData = {
    serviceType: "General Contracting",
    serviceName: "Ontario General Contracting Services",
    services: [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Residential Construction Ontario",
          "description": "Custom homes, renovations, and residential additions across Ontario"
        }
      },
      {
        "@type": "Offer", 
        "itemOffered": {
          "@type": "Service",
          "name": "Commercial Construction Ontario",
          "description": "Office buildings, retail spaces, and commercial developments"
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOMeta 
        title="General Contractor — Residential & Commercial | Niagara • Hamilton • GTA"
        description="Full-service construction management for homes and businesses across Niagara, Hamilton & the GTA. Reliable timelines and quality results."
        keywords="general contractor Niagara, Hamilton construction services, GTA contractor, project management, commercial builds, design-build, permits"
        canonicalUrl="https://blueknightcontracting.ca/services/general-contracting"
      />
      <SchemaMarkup type="Service" data={serviceData} />
      <SchemaMarkup type="FAQ" data={faqData} />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-glow text-primary-foreground py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            Trusted Ontario General Contractors for Every Project
          </h1>
          <p className="text-xl opacity-90 max-w-4xl mx-auto mb-8">
            At Blue Knight Contracting and Construction, we provide complete general contracting and project management 
            services for residential, commercial, and industrial projects across Ontario. From breaking ground to final 
            handover, our experienced team ensures projects are delivered on time, within budget, and built to last.
          </p>
          <p className="text-lg opacity-80 mb-8">
            We proudly serve communities across Ontario, including Toronto, Ottawa, Mississauga, Hamilton, London, 
            Windsor, Kingston, Barrie, Sudbury, and Thunder Bay.
          </p>
          <Button variant="hero" size="lg" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90">
            <Phone className="mr-2 h-5 w-5" />
            Request Free Consultation
          </Button>
        </div>
      </section>

      {/* Service Image and Overview */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={blueprintsImage} 
                alt="Ontario general contractor reviewing construction blueprints with project team"
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6">Complete Project Management Excellence</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Our general contracting services encompass every aspect of your construction project. 
                From initial planning and design coordination to final walkthrough, we serve as your 
                single point of contact, ensuring seamless communication and exceptional results.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="flex items-start gap-3">
                  <Shield className="h-6 w-6 text-primary mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Licensed & Insured</h3>
                    <p className="text-sm text-muted-foreground">Fully licensed across Ontario</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Award className="h-6 w-6 text-primary mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Quality Guaranteed</h3>
                    <p className="text-sm text-muted-foreground">Commitment to excellence</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-6 w-6 text-primary mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">On-Time Delivery</h3>
                    <p className="text-sm text-muted-foreground">Projects completed as scheduled</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Target className="h-6 w-6 text-primary mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Budget Control</h3>
                    <p className="text-sm text-muted-foreground">Transparent cost management</p>
                  </div>
                </div>
              </div>
              
              <Link to="/contact">
                <Button variant="cta" size="lg">Get Your Project Quote</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our General Contracting Services */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Our General Contracting Services
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive construction management and contracting services for projects of all sizes across Ontario.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit">
                  <FileText className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Complete Project Management</CardTitle>
                <CardDescription>Coordinating every phase from design to completion</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Design coordination and planning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Permit acquisition and approvals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Timeline and milestone management</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit">
                  <Building className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Residential Construction</CardTitle>
                <CardDescription>Custom homes, renovations, and additions</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Custom home construction</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Home renovations and remodeling</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Home additions and extensions</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit">
                  <Building className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Commercial Builds</CardTitle>
                <CardDescription>Offices, retail spaces, and multi-use developments</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Office building construction</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Retail and restaurant spaces</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Mixed-use developments</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit">
                  <Building className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Industrial Projects</CardTitle>
                <CardDescription>Warehouses, factories, and heavy-use facilities</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Manufacturing facility construction</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Warehouse and distribution centers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Heavy industrial facilities</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Subcontractor Coordination</CardTitle>
                <CardDescription>Skilled trades managed with precision</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Licensed trade coordination</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Quality control and oversight</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Schedule coordination</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit">
                  <Target className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Budgeting & Scheduling</CardTitle>
                <CardDescription>Cost control and project timeline management</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Detailed cost estimation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Budget tracking and control</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Project timeline optimization</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Blue Knight Contracting */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Why Choose Blue Knight Contracting?
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Proven Experience Across Ontario</h3>
                    <p className="text-muted-foreground">
                      Extensive experience across Ontario's diverse construction landscape with projects ranging 
                      from custom homes to large commercial developments.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">One Point of Contact</h3>
                    <p className="text-muted-foreground">
                      Seamless project management with a single point of contact for streamlined communication 
                      and accountability throughout your project.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Strong Trade Partnerships</h3>
                    <p className="text-muted-foreground">
                      Established partnerships with trusted trades and suppliers across Ontario, ensuring 
                      quality materials and skilled craftsmanship on every project.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Safety-First Approach</h3>
                    <p className="text-muted-foreground">
                      Comprehensive safety protocols meeting Ontario building codes and regulations, 
                      with ongoing safety training for all team members.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Transparent Communication</h3>
                    <p className="text-muted-foreground">
                      Regular project updates, transparent pricing, and commitment to quality with 
                      clear communication throughout every phase of construction.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div>
              <img 
                src={projectsImage} 
                alt="General contracting projects in Ontario including custom homes and commercial buildings"
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas in Ontario */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Service Areas in Ontario
            </h2>
            <p className="text-lg text-muted-foreground max-w-4xl mx-auto mb-8">
              Blue Knight Contracting and Construction offers general contracting services throughout Ontario, including:
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-lg">Greater Toronto Area</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>Toronto</li>
                  <li>Mississauga</li>
                  <li>Brampton</li>
                  <li>Vaughan</li>
                  <li>Markham</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-lg">Eastern Ontario</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>Ottawa</li>
                  <li>Kingston</li>
                  <li>Cornwall</li>
                  <li>Belleville</li>
                  <li>Peterborough</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-lg">Southwestern Ontario</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>London</li>
                  <li>Windsor</li>
                  <li>Kitchener-Waterloo</li>
                  <li>Cambridge</li>
                  <li>Sarnia</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-lg">Northern Ontario</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>Sudbury</li>
                  <li>Thunder Bay</li>
                  <li>North Bay</li>
                  <li>Sault Ste. Marie</li>
                  <li>Timmins</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Common questions about our general contracting services across Ontario.
            </p>
          </div>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            {faqData.faqs.map((faq, index) => (
              <Card key={index} className="border-2 border-primary/10">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-lg mb-3 text-primary">
                    Q: {faq.question}
                  </h3>
                  <p className="text-muted-foreground">
                    A: {faq.answer}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Get Started Today */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Get Started Today
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
            Looking for a reliable general contractor in Ontario? Contact Blue Knight Contracting and Construction 
            today to discuss your project and request a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button variant="hero" size="lg" className="text-lg px-8 py-4 bg-primary-foreground text-white hover:bg-primary-foreground/90">
              <Phone className="mr-2 h-5 w-5" />
              Call (416) 555-BLUE
            </Button>
            <Button variant="contact" size="lg" className="text-lg px-8 py-4">
              <Mail className="mr-2 h-5 w-5" />
              Request Free Consultation
            </Button>
          </div>
          <div className="text-lg opacity-80">
            <p>📍 Serving all of Ontario</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GeneralContractingServices;